import { App, Plugin } from 'vue';
import { I18n } from '@/plugins/localization';
import { Alerts } from '@/plugins/alerts';
import { Sitemap } from '@/plugins/sitemap';
import AuthService, { AuthModel, TokenModel } from '@/modules/core/auth/services/AuthService';
import { RouteLocationRaw as Location, RouteLocation as Route } from 'vue-router';
import { Store } from 'vuex';
import Cookies from 'js-cookie';
import { useOneSignal } from '@onesignal/onesignal-vue3';
import * as Sentry from "@sentry/vue";

interface AuthState
{
    loaded: boolean;
    authenticated: boolean;
    identity: AuthModel;
    rememberMe: boolean;
    token: string;
    impersonated: number;
}

export interface Auth
{
    login(username: string, password: string, rememberMe?: boolean, forceLogin?: boolean): Promise<void>;
    logout(redirect?: Location): Promise<void>;
    validate(throwError?: boolean): Promise<TokenModel>;
    refresh(throwError?: boolean): Promise<TokenModel>;
    recover(throwError?: boolean): Promise<TokenModel>;
    ntlm(throwError?: boolean): Promise<TokenModel>;
    token(): string;
    claims(): Record<string, string>;
    fetch(): void;
    user(): AuthModel;
    userId(): number;
    check(): boolean;
    ready(): boolean;
    reload(): void;
    impersonating(): number;
    impersonatingValue(): string;
    impersonate(id: number): void;
    unimpersonate(): void;
    loginToOneSignal(): Promise<void>;
}

interface AuthRedirect
{
    homePage: Location;
    loginPage: Location;
    forbidden: Location;
    notFound: Location;
    changePassword: Location;
}

export class AuthOptions
{
    public store: Store<any>;
    public routes: AuthRedirect;
    public defaultPolicy: (token: Record<string, string>) => boolean = (token: Record<string, string>) => true;
}

export class AuthHelper implements Auth
{
    private vue: any;
    private router: any;
    private alert: Alerts;
    private i18n: I18n;
    private sitemap: Sitemap;
    private store: Store<any>;
    private options: AuthOptions;
    private sessionIntervalHandler: any;
    private tokenTimeoutHandler: any;

    public constructor(app: App<any>, options: AuthOptions)
    {
        const vue = app.config.globalProperties;

        this.router = vue.$router;
        this.sitemap = vue.$sitemap;
        this.alert = vue.$alert;
        this.i18n = vue.$i18n;
        this.store = options.store;
        this.options = options;

        this.initStore();
        this.initRouter();
        this.initSession();
        this.initImpersonation();
    }

    private initStore(): void
    {
        this.store.registerModule('auth', {
            namespaced: true,
            state: {
                loaded: false,
                authenticated: false,
                identity: null,
                rememberMe: false,
                token: null,
                impersonated: null,
                subscribedPlayerId: null
            },
            getters: {
                reservationColors: (state) =>
                {
                    if (!state.identity || !state.identity.reservationColors) return {};

                    return state.identity.reservationColors;
                }
            },
            mutations: {
                setLoaded(state, value: boolean)
                {
                    state.loaded = value;
                },
                setAuthenticated(state, value: boolean)
                {
                    state.authenticated = value;
                },
                setIdentity(state, identity: AuthModel)
                {
                    state.identity = identity;
                },
                clearIdentity(state)
                {
                    state.identity = null;
                },
                rememberMe(state, enabled: boolean)
                {
                    state.rememberMe = enabled;
                },
                setToken(state, token: string)
                {
                    localStorage.setItem('token', token);
                    state.token = token;
                },
                impersonate(state, id: number)
                {
                    state.impersonated = id;
                },
                unimpersonate(state)
                {
                    state.impersonated = null;
                }
            }
        }, {
            preserveState: false
        });
    }

    private initRouter(): void
    {
        this.router.beforeEach(async (to: Route, from: Route, next: any) =>
        {
            if (this.state.loaded === false)
            {
                // Sprawdzenie ważności access tokena po odświeżeniu przeglądarki
                if (!this.check())
                {
                    await this.validate();
                }

                // Uzyskanie nowego access tokena za pomocą refresh tokena (opcja "remember me")
                if (!this.check())
                {
                    await this.recover();
                }

                // Logowanie Windows
                if (!this.check() && import.meta.env.VITE_APP_WIN_AUTH == "true")
                {
                    await this.ntlm();
                }

                // Pobranie danych użytkownika
                if (this.check())
                {
                    await this.fetch();
                }

                this.store.commit('auth/setLoaded', true);
            }

            next();
        });

        this.router.beforeEach(async (to: Route, from: Route, next: any) =>
        {
            if (to.meta && to.meta.hasOwnProperty('auth'))
            {
                const auth: any = to.meta.auth;

                // Wymuszenie zmiany hasła
                if (auth !== false && this.state.authenticated === true && this.state.identity.forcePasswordChange === true && to.meta.stop !== true)
                {
                    this.alert.info(this.i18n.$t('[[[Twoje hasło wygasło. Zmień je na nowe.]]]'));
                    next(this.options.routes.changePassword);

                    return;
                }

                if (typeof (auth) === "boolean")
                {
                    if (auth === true && this.state.authenticated === false)
                    {
                        next(this.options.routes.loginPage);

                        return;
                    }

                    if (auth === false && this.state.authenticated === true)
                    {
                        next(this.options.routes.homePage);

                        return;
                    }

                    if (this.state.authenticated === true)
                    {

                        // Pobranie aktualnego widoku z sitemapa
                        const node = await this.sitemap.find(to);

                        // Sprawdzenie uprawnień do widoku w sitemapie
                        if (node && node.allowed == false)
                        {
                            const userId = this.store.state.auth.identity.publicId || 'undefined - store nie ma takiej wartości';
                            const userName = this.store.state.auth.identity.userName || 'undefined - store nie ma takiej wartości';
                            const roles = this.store.state.auth.identity.departments || 'undefined - store nie ma takiej wartości';
                            const authAll = node?.auth?.all;
                            const authAny = node?.auth?.any;

                            Sentry.withScope((scope) =>
                            {
                                scope.setExtra('userId', userId);
                                scope.setExtra('userName', userName);
                                scope.setExtra('roles', roles);
                                scope.setExtra('to', to);
                                scope.setExtra('sitemapNode', node);
                                scope.setExtra('authAll', authAll);
                                scope.setExtra('authAny', authAny);
                                Sentry.captureException(new Error('403 - Nie można uzyskać dostępu'));
                            }
                            );

                            next(this.options.routes.forbidden);

                            return;
                        }
                    }
                }

                if (Array.isArray(auth) && auth.length > 0)
                {
                    if (this.state.authenticated === false)
                    {
                        next(this.options.routes.loginPage);

                        return;
                    }
                }
            }

            next();
        });
    }

    private initSession(): void
    {
        const sessionLifetime = 60; // minuty
        const activityInterval = 5; // sekundy

        document.body.addEventListener('click', () =>
        {
            this.lastActivity = Date.now();
        });

        this.sessionIntervalHandler = setInterval(() =>
        {
            this.lastActivity = this.lastActivity || Date.now();

            // Z włączoną opcją "remember me" sesja nigdy nie wygasa
            if (this.check() && this.state.rememberMe === false)
            {
                const timeLeft = (this.lastActivity + sessionLifetime * 60 * 1000) - Date.now();

                if (timeLeft < 0)
                {
                    this.logout();
                }
            }
        },
        activityInterval * 1000);

        window.addEventListener('storage', (e) =>
        {
            if (e.key === 'last-activity' && e.oldValue != null && e.newValue == null && e.storageArea !== localStorage)
            {
                // this.clearAll();
                // this.router.push(this.options.routes.loginPage).catch(() => {});

                // natychmiastowe wylogowanie po usunięciu last-activity z localstorage
                this.logout();
            }
        });
    }

    private initImpersonation(): void
    {
        const impersonated = localStorage.getItem("impersonated");

        if (impersonated)
        {
            this.store.commit('auth/impersonate', Number(impersonated));
        }

        window.addEventListener('storage', (e) =>
        {
            if (e.key === "impersonated")
            {
                if (e.newValue != null)
                    this.store.commit('auth/impersonate', Number(e.newValue));
                else
                    this.store.commit('auth/unimpersonate');
            }
        });
    }

    private get state(): AuthState
    {
        return this.store.state.auth as AuthState;
    }

    private get lastActivity(): number
    {
        return Number(localStorage.getItem("last-activity") || 0);
    }

    private set lastActivity(value: number)
    {
        localStorage.setItem("last-activity", value.toString());
    }

    private clearSession(): void
    {
        localStorage.removeItem("last-activity");
    }

    private decryptToken(token: string): any
    {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));

        return payload;
    }

    private setToken(tokens: TokenModel): void
    {
        if (tokens.token.length > 0)
        {
            this.store.commit('auth/setToken', tokens.token);
            this.store.commit('auth/setAuthenticated', true);

            const clockSkew = 60 * 1000;
            const payload = this.decryptToken(tokens.token);
            const timeLeft = Number(payload.exp) * 1000 - (new Date()).getTime() - clockSkew;

            if (timeLeft > 0)
            {
                this.tokenTimeoutHandler = setTimeout(async () =>
                {
                    try
                    {
                        this.refresh();
                    }
                    catch (ex)
                    {
                        this.logout();
                    }
                },
                timeLeft);
            }
        }
    }

    private clearToken(): void
    {
        clearTimeout(this.tokenTimeoutHandler);
        this.store.commit('auth/setToken', null);
        this.store.commit('auth/setAuthenticated', false);
    }

    private clearAll(): void
    {
        this.store.commit('auth/rememberMe', false);
        this.store.commit('auth/clearIdentity');
        this.unimpersonate();
        this.clearSession();
        this.clearToken();
    }

    private checkPermissions(token: Record<string, string>): void
    {
        if (this.options.defaultPolicy(token) == false)
        {
            throw (() =>
            {
                return {
                    code: 400,
                    message: "[[[Nie masz uprawnień do panelu administracyjnego.]]]",
                    data: null as any,
                    inner: null as any
                };
            })();
        }
    }

    public async login(username: string, password: string, rememberMe: boolean = false, forceLogin: boolean = false): Promise<void>
    {
        try
        {
            this.clearAll();

            const result = await AuthService.login(username, password, rememberMe, forceLogin);
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            await this.fetch();

            this.store.commit('auth/rememberMe', rememberMe);
            this.router.push(this.options.routes.homePage).catch(() => {});

            // await this.loginToOneSignal();
        }
        catch (ex)
        {
            this.clearAll();

            throw ex;
        }
    }

    public async logout(redirect?: Location): Promise<void>
    {
        try
        {
            // const oneSignal = useOneSignal();

            // await oneSignal.logout();

            this.clearToken();
            await AuthService.logout();

            location.reload();
        }
        catch (ex)
        {
            this.clearAll();

            throw ex;
        }
    }

    public async validate(throwError: boolean = false): Promise<TokenModel>
    {
        try
        {
            const result = await AuthService.validateToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            this.store.commit('auth/rememberMe', result.refresh !== null);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async refresh(throwError: boolean = false): Promise<TokenModel>
    {
        try
        {
            const result = await AuthService.refreshToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async recover(throwError: boolean = false): Promise<TokenModel>
    {
        try
        {
            const result = await AuthService.recoverToken();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            this.store.commit('auth/rememberMe', true);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public async ntlm(throwError: boolean = false): Promise<TokenModel>
    {
        try
        {
            const result = await AuthService.ntlm();
            const token = this.decryptToken(result.token);

            this.checkPermissions(token);
            this.setToken(result);

            return token;
        }
        catch (ex)
        {
            this.clearAll();

            if (throwError)
                throw ex;
        }

        return null;
    }

    public token(): string
    {
        const token = localStorage.getItem('token') === 'null' ? null : localStorage.getItem('token');

        return token;
    }

    public claims(): Record<string, string>
    {
        return this.decryptToken(this.token());
    }

    public async fetch(): Promise<void>
    {
        try
        {
            const result = await AuthService.getIdentity(true);

            this.store.commit('auth/setIdentity', result);
        }
        catch (ex)
        {
            this.clearAll();
        }
    }

    public user(): AuthModel
    {
        return this.state.identity;
    }

    public userId(): number
    {
        return this.impersonating() || this.user().id;
    }

    public check(): boolean
    {
        return this.state.authenticated;
    }

    public ready(): boolean
    {
        return this.state.loaded;
    }

    public reload(): void
    {
        this.store.commit('auth/setLoaded', false);
        setTimeout(() => this.store.commit('auth/setLoaded', true), 100);
    }

    public impersonating(): number
    {
        return this.state.impersonated as number;
    }

    public impersonatingValue(): string
    {
        const payload = this.decryptToken(this.token());
        const value = window.btoa(`${this.impersonating().toString()}#${payload.jti}`);

        if (Cookies.get('access-impersonate') != value)
        {
            Cookies.set('access-impersonate', value, { path: '/', secure: true, sameSite: 'None' });
        }

        return value;
    }

    public impersonate(id: number): void
    {
        this.store.commit('auth/impersonate', id);
        localStorage.setItem("impersonated", id.toString());
    }

    public unimpersonate(): void
    {
        this.store.commit('auth/unimpersonate');
        localStorage.removeItem("impersonated");
        Cookies.remove('access-impersonate', { path: '/', secure: true, sameSite: 'None' });
    }

    public async loginToOneSignal(): Promise<void>
    {
        const user = await this.user();

        const oneSignal = useOneSignal();

        if (user.publicId && oneSignal)
        {
            const notifications = oneSignal.Notifications;

            oneSignal.User.addTag('device_type', 'desktop');

            if (!notifications.permission)
                await oneSignal.Notifications.requestPermission();

            if (!notifications.permission || !notifications.isPushSupported())
            {
                this.alert.error(this.i18n.$t('[[[Aplikacja nie ma uprawnień do wysyłania powiadomień PUSH]]]'));

                return;
            }

            await oneSignal.login(user.publicId);

            // let attepmts = 0;

            // const interval = setInterval(async () =>
            // {
            //     if (attepmts === 4)
            //         clearInterval(interval);

            //     console.log(user.publicId);
            //     await oneSignal.login(user.publicId);
            //     attepmts++;
            // }, 500);
        }
    }
}

export const adminAccessPolicy = (token: Record<string, string>): boolean =>
{
    const claimName = "https://ideo.pl/identity/claims/permission";
    const claimValue = "AdminAccess";

    return claimName in token && ([].concat(token[claimName])).includes(claimValue);
};

const AuthPlugin: Plugin =
{
    install(app, options)
    {
        const vue = app.config.globalProperties;

        if (!vue.$router)
        {
            throw new Error("Vue:router must be set.");
        }

        if (!vue.$sitemap)
        {
            throw new Error("Vue:sitemap must be set.");
        }

        if (!vue.$alert)
        {
            throw new Error("Vue:alert must be set.");
        }

        if (!vue.$i18n)
        {
            throw new Error("Vue:i18n must be set.");
        }

        if (!options || !options.store)
        {
            throw new Error("AuthOptions.store must be set.");
        }

        app.config.globalProperties.$auth = new AuthHelper(app, options);
    }
};

export default AuthPlugin;

declare module "@vue/runtime-core"
{
    interface ComponentCustomProperties
    {
        $auth: Auth;
    }
}
